<template>
  <div>
    <vue-headful
      :title="getIsDuplicate ? this.setPageTitle('efficiencyTestCreate') : this.setPageTitle('efficiencyTestEdit')"
    />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>

    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            <template v-if="getIsDuplicate">{{ $t('efficiencyTestDetails.title') }}</template>
            <template v-else>{{ $t('efficiencyTestEdit.title') }}</template>
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="cancelEditingProfile">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button size="sm" variant="primary" :disabled="!canSave" @click="saveScoringProfile()">
            {{ $t('common.save') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- NAME + DESCRIPTION -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <div class="mr-2 w-50">
              <span>{{ $t('common.name') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="nameFormatter"
                v-model="scoringProfileName"
                :placeholder="$t('efficiencyTestDetails.name')"
              ></b-input>
            </div>
            <div class="w-50">
              <span>{{ $t('common.description') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="descriptionFormatter"
                v-model="scoringProfileDescription"
                :placeholder="$t('efficiencyTestDetails.description')"
              ></b-input>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- PRODUCT UNDER TEST -->
      <b-row class="mb-4">
        <b-col>
          <div class="product-test-color mb-3">
            {{ $t('efficiencyTestDetails.productUnderTesting') }}
          </div>
          <div class="border-rounded bg-white p-3">
            <div class="product-test-color">
              {{ selectedProduct && selectedProduct.name ? selectedProduct.name : $t('common.notAvailable') }}
            </div>
            <div class="d-flex align-items-center flex-wrap mt-3 product-test-color">
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.isin') }}:</span>
              <span class="p-sm-1">{{ productField('isin') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.acepiCategory') }}:</span>
              <span class="p-sm-1"> {{ productField('acepiCategory') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.maturityDate') }}:</span>
              <span class="p-sm-1">{{ productField('maturityDate') }}</span
              ><span class="px-1">-</span>
              <span class="font-weight-bold">{{ $t('productDetails.fields.Characteristics.currency') }}:</span>
              <span class="p-sm-1">{{ productField('currency') }}</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- PROFILE COVERAGE -->
      <b-row class="mb-4">
        <b-col>
          <profile-coverage :dataCoverage="getProfileCoverage"></profile-coverage>
        </b-col>
      </b-row>

      <!-- TABS -->
      <b-row class="mb-4">
        <b-col>
          <b-tabs
            v-model="currentTab"
            class="fairmat-primary-tabs"
            @activate-tab="onActivateTab"
            data-test="tabsScoringDefinition"
          >
            <b-tab :title="$t('efficiencyTestDetails.comparisonCriteria.title')">
              <comparison-criteria ref="comparisonCriteria"></comparison-criteria>
            </b-tab>
            <b-tab>
              <template #title>
                {{ $t('efficiencyTestDetails.profileCoverageDetail') }}
                <b-badge v-if="hasPeersCheck" pill variant="secondary" class="ml-1">{{ peersTotal }}</b-badge>
              </template>
              <tab-profile-coverage ref="tabProfileCoverage"></tab-profile-coverage>
            </b-tab>
            <b-tab :title="$t('efficiencyTestDetails.scoringDefinition')">
              <tab-scoring-definition></tab-scoring-definition>
            </b-tab>
          </b-tabs>

          <div class="d-flex align-items-center justify-content-end mt-3">
            <span v-if="!canSave" class="small text-muted mx-3">{{
              $t('efficiencyTestDetails.saveRequirements.intro', { missing: saveMissing.join(', ') })
            }}</span
            ><button :disabled="!canSave" class="btn btn-sm btn-primary" @click="saveScoringProfile({ execute: true })">
              <i class="fas fa-sync-alt"></i>
              {{ $t('efficiencyTestDetails.saveAndRun') }}
            </button>
          </div>
          <div class="text-right text-muted small mt-3">
            {{ $t('efficiencyTestDetails.saveAndRunDuration') }}
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
// Same shell as legacy EfficiencyTestsEdit.vue; the first tab now shows the comparison-criteria
// panel (mock/CreazioneTest.vue) instead of the catalog-driven filters panel. Load/save go through
// `efficiencyTestsEditor` (POST /api/efficiencyTests/save, GET /api/efficiencyTests/{id}).
import TabProfileCoverage from './TabProfileCoverage.vue'
import TabScoringDefinition from './TabScoringDefinition.vue'
import ProfileCoverage from '../../components/EfficiencyTests/ProfileCoverage.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import ComparisonCriteria from '../../components/EfficiencyTests/ComparisonCriteria.vue'
import unsavedChangesMixin from '../../mixins/unsavedChanges'
import { efficiencyTestSnapshot } from '../../utils/unsavedChangesSnapshots'
import efficiencyTestSaveRulesMixin from '../../mixins/efficiencyTestSaveRules'
import { editTabIndex } from '../../utils/efficiencyTestEditTabs'
import { detailFieldDisplayValue } from '../../utils/detailFieldFormat'

export default {
  mixins: [unsavedChangesMixin, efficiencyTestSaveRulesMixin],

  components: {
    CustomFlexHeader,
    ProfileCoverage,
    TabProfileCoverage,
    TabScoringDefinition,
    ComparisonCriteria,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      copy: 'copy_',
      // A Failed test duplicated opens on the peer tab, where its reasons are (see utils/efficiencyTestEditTabs.js).
      currentTab: editTabIndex(this.$route && this.$route.query, (this.id || '').includes('copy_')),
      maxProfileNameLength: 128,
      maxProfileDescriptionLength: 255,
    }
  },

  computed: {
    getLoading: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getLoading']
      },
    },
    getProfileId: {
      get() {
        let id = this.id
        return id.replace(this.copy, '')
      },
    },
    scoringProfileName: {
      get() {
        const name = this.$store.getters['efficiencyTestsCommon/getScoringProfileName']
        return this.getEditingMode() !== 'duplicate' ? name : this.formatScoringProfileName(name)
      },
      set(newVal) {
        this.$store.dispatch('efficiencyTestsCommon/setScoringProfileName', this.nameFormatter(newVal))
      },
    },
    scoringProfileDescription: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getScoringProfileDescription']
      },
      set(newVal) {
        this.$store.dispatch('efficiencyTestsCommon/setScoringProfileDescription', this.descriptionFormatter(newVal))
      },
    },
    // The counters come from the peers check (the same one that feeds the peer tab), not from the profile
    // coverage of the legacy scoring profiles, which is never filled for an efficiency test.
    getProfileCoverage() {
      return this.$store.getters['efficiencyTestsCoverageDetail/getCoverageCheckStats']
    },
    getIsDuplicate: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getIsDuplicate']
      },
    },
    // The count of the tab is all the peers considered, not how many reasons there are.
    peersTotal() {
      return this.$store.getters['efficiencyTestsCoverageDetail/getCoverageCheckItems'].length
    },
    hasPeersCheck() {
      return this.$store.getters['efficiencyTestsCoverageDetail/getCoverageCheckResult'] !== null
    },
    selectedProduct() {
      return this.$store.getters['efficiencyTestsCommon/getSelectedProduct']
    },
  },

  methods: {
    unsavedChangesSnapshot() {
      return efficiencyTestSnapshot(this)
    },

    unsavedChangesReady() {
      return !this.getLoading
    },

    // Looks up `key` in the product's Characteristics sidebar fields and formats it exactly like the product
    // detail page does — same as EfficiencyTestsCreate.vue. "N/D" until the product is loaded.
    productField(key) {
      const characteristics = (this.selectedProduct && this.selectedProduct.characteristics) || []
      const field = characteristics.find((f) => f.key === key)
      if (!field) {
        return this.$t('common.notAvailable')
      }
      return detailFieldDisplayValue(this, field).text
    },

    // GET /api/efficiencyTests/{id} only returns the testedProductId, which the editor stores as a bare
    // { productId }: the name and the characteristics are read here, the way the create page does. A
    // product no longer among the company products leaves the block on "N/D".
    loadTestedProduct() {
      const current = this.selectedProduct
      if (!current || !current.productId || current.characteristics) {
        return Promise.resolve()
      }
      return this.$store
        .dispatch('efficiencyTestsCommon/loadSelectedProductById', current.productId)
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
    },

    getEditingMode() {
      return this.id.includes(this.copy) ? 'duplicate' : 'update'
    },

    formatScoringProfileName(name) {
      return name.replace(/Copy|Copia/, this.$t('efficiencyTestEdit.copy'))
    },

    nameFormatter(value) {
      return value.substring(0, this.maxProfileNameLength)
    },
    descriptionFormatter(value) {
      return value.substring(0, this.maxProfileDescriptionLength)
    },

    onActivateTab(newTabIndex, prevTabIndex, firedEvent) {
      if (newTabIndex === 1) {
        // Re-run the coverage check with whatever the user last toggled in the Criteri tab. Tabs are
        // not lazy, so this is the only point where the criteria are known to be final.
        if (this.$refs.tabProfileCoverage && this.$refs.comparisonCriteria) {
          this.$refs.tabProfileCoverage.refreshCoverage(this.$refs.comparisonCriteria.getCriteriaPayload())
        }
      }
    },

    cancelEditingProfile() {
      this.$router.push({ name: 'efficiencyTestsList' })
    },

    saveScoringProfile({ execute = false } = {}) {
      const results = this.$store.getters['efficiencyTestsCommon/getScoringProfileDefinitionValidation']
      const isValidName = this.$store.getters['efficiencyTestsCommon/getScoringProfileNameValidation']
      const modelId = this.$store.getters['efficiencyTestsDefinition/getSelectedModelId']
      const sortedDrivers = this.$store.getters['efficiencyTestsDefinition/getSortedModelDrivers']
      const driverFamiliesList = this.$store.getters['efficiencyTestsDefinition/getUnsortedModelDrivers']

      if (!isValidName) {
        this.$bvToast.toast(this.$t('efficiencyTests.errorOnMissingName'), {
          title: this.$t('efficiencyTests.scoringDefinition'),
          variant: 'danger',
        })
      }

      // if model selected NO need to validate scoring definition
      if (!modelId) {
        if (!results.isValidScoringDefinition) {
          this.$bvToast.toast(this.$t('efficiencyTests.errorOnDriversSelected'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (results.isValidScoringDefinition && !results.isValidScoringDefinitionTotalWeight) {
          this.$bvToast.toast(this.$t('efficiencyTests.errorOnTotalWeight'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (!results.isDriversWeightsGreaterThanZero) {
          this.$bvToast.toast(this.$t('efficiencyTests.weightsMustBeGreaterThan0'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }
      } else {
        if (driverFamiliesList.length !== sortedDrivers.length) {
          this.$bvToast.toast(this.$t('efficiencyTests.allDriversMustBeSelectedAndSorted'), {
            title: this.$t('efficiencyTests.scoringDefinition'),
            variant: 'danger',
          })
        }
      }

      const isValid =
        isValidName &&
        (modelId
          ? driverFamiliesList.length === sortedDrivers.length
          : Object.values(results).every((j) => j !== false))

      if (isValid) {
        const criteria = this.$refs.comparisonCriteria ? this.$refs.comparisonCriteria.getCriteriaPayload() : []
        this.$store
          .dispatch('efficiencyTestsEditor/saveScoringProfile', criteria)
          .then((data) => {
            const afterSave = execute ? this.executeSavedTest(data && data.id) : Promise.resolve()
            return afterSave.then(() => this.$router.push({ name: 'efficiencyTestsList' }))
          })
          .catch(async (error) => {
            const err = await error.response
            const message = await err.text()
            if (message.includes('NameInUse')) {
              this.$bvToast.toast(this.$t('efficiencyTests.nameInUse'), {
                title: this.$t('efficiencyTests.title'),
                variant: 'danger',
              })
            } else if (message.includes('ScoringDefinitionWeightInvalid')) {
              this.$bvToast.toast(this.$t('efficiencyTests.scoringDefinitionWeightInvalid'), {
                title: this.$t('efficiencyTests.title'),
                variant: 'danger',
              })
            } else {
              this.$bvModal.msgBoxOk(this.$t('common.genericError'))
            }
          })
      }
    },
  },

  created() {
    this.$store.dispatch('efficiencyTestsCommon/newScoringProfile')
    // Models are a shared catalog, not EfficiencyTests-specific — see TabScoringDefinition.vue.
    this.$store.dispatch('scoringProfilesModelsList/loadModelsListToAssociate').catch((err) => {
      this.httpErrorsHandler(err)
    })
    this.$store
      .dispatch('efficiencyTestsEditor/loadScoringProfile', {
        id: this.getProfileId,
        action: this.getEditingMode(),
      })
      .then((criteria) => {
        this.loadTestedProduct()
        if (this.$refs.comparisonCriteria) {
          this.$refs.comparisonCriteria.setCriteria(criteria)
          // The peers count enables the save buttons: it must be known without opening the coverage tab.
          if (this.$refs.tabProfileCoverage) {
            this.$refs.tabProfileCoverage.refreshCoverage(this.$refs.comparisonCriteria.getCriteriaPayload())
          }
        }
      })
      .catch((err) => {
        if (err && err.readyNotEditable) {
          this.$bvModal.msgBoxOk(this.$t('efficiencyTestEdit.readyNotEditable'))
          this.$router.replace({ name: 'efficiencyTestsList' })
          return
        }
        this.httpErrorsHandler(err)
      })
  },
}
</script>

<style lang="scss" scoped>
.product-test-color {
  color: #212529;
}

.border-rounded {
  border: 1px solid rgb(229, 229, 229);
  border-radius: 8px;
  padding: 22px 22px 26px;
  box-shadow: rgba(0, 0, 0, 0.03) 0px 1px 2px;
  background: rgb(255, 255, 255);
}
</style>
